import { NavigationUser } from "./NavigationUser";
import { Button } from "components/atoms/Button";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "store/modalSlice";
import { RootState } from "store";
import { NavigationIconButton } from "./NavigationIconButton";
import { Stack } from "components/atoms/Stack";
import { FaBars, FaPrint, FaRobot, FaUserPlus } from "react-icons/fa";
import styled, { useTheme } from "styled-components";
import { NavigationPrimaryLinks, getPageTitle } from "./NavigationPrimaryLinks";
import { NotificationBell } from "./NotificationBell";
import { NavigationCreateDropdown } from "./NavaigationCreateDropdown";
import { NavigationSyncDropdown } from "./NavigationSyncDropdown";
import { MobileMenuDrawer } from "./MobileMenuDrawer";
import { CustomerSelect } from "components/molecules/CustomerSelect";
import { PermissionCheck } from "utils/PermissionCheck/PermissionCheck";
import { isUserAdmin } from "utils/isUserAdmin";
import { Roles } from "enum/roles.enum";
import { useIsAccountRestricted } from "hooks/useIsAccountRestricted";
import { useState } from "react";
import { useMedia } from "react-use";
import { useRouter } from "next/router";

const StyledNavigation = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 100%;
  border-bottom: 1px solid;
  border-color: ${(props) => `${props.theme.borderColor}`};
  padding-left: ${(props) => `${props.theme.gutter}`};
  padding-right: ${(props) => `${props.theme.gutter}`};
  background-color: ${(props) => `${props.theme.navigationBackgroundColor}`};
  height: ${(props) => `${props.theme.navigationHeight}`};

  /* Phone-sized viewports: the fixed-height single row cannot hold the account
     picker plus the action buttons, so let it wrap into rows. */
  @media (max-width: 767px) {
    flex-wrap: wrap;
    height: auto;
    min-height: ${(props) => `${props.theme.navigationHeight}`};
    padding-top: 8px;
    padding-bottom: 8px;
    row-gap: 8px;
  }
`;

export const Navigation = () => {
  const dispatch = useDispatch();

  const { loggedInUser } = useSelector((state: RootState) => {
    return state.authSlice;
  });

  const permissions = new PermissionCheck({ user: loggedInUser });

  const isRestricted = useIsAccountRestricted();

  // Demo environment only (NEXT_PUBLIC_DEMO_MODE) and ITD staff/sales roles.
  const canCreateDemoAccount = permissions.demoAccounts.canCreate();

  const isITDAdminForSalesAccounts = loggedInUser[
    "https://connexx.com/roles"
  ]?.includes(Roles.ITD_ADMIN_FOR_SALES_ACCOUNTS);

  // ITD staff don't receive customer-facing notifications.
  const isStaff = isUserAdmin(loggedInUser);

  const theme = useTheme();
  const { pathname } = useRouter();
  const [isMenuOpen, setMenuOpen] = useState(false);
  // Phone-sized viewports get the compact header: menu button, page title,
  // notification bell and user menu, with the account picker on its own row.
  // Defaults to false so SSR renders the desktop layout.
  const isMobileViewport = useMedia("(max-width: 767px)", false);

  if (isMobileViewport) {
    return (
      <>
        <StyledNavigation>
          <div className="flex items-center w-full min-w-0 gap-2">
            <button
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="w-9 h-9 flex-none flex items-center justify-center rounded-md border text-gray12"
              style={{ borderColor: theme.borderColor }}
            >
              <FaBars size={15} />
            </button>
            <div className="text-base font-semibold leading-none truncate">
              {getPageTitle(pathname)}
            </div>
            <div className="flex-1" />
            {!isRestricted && (
              <NavigationCreateDropdown
                toggle={
                  <Button colorScheme="primary" className="whitespace-nowrap !px-3">
                    Create
                  </Button>
                }
              />
            )}
            {!isStaff && <NotificationBell />}
            <NavigationUser />
          </div>
          {permissions.salesAccounts.canChange() && (
            <div className="w-full min-w-0">
              <CustomerSelect />
            </div>
          )}
        </StyledNavigation>
        <MobileMenuDrawer
          isOpen={isMenuOpen}
          onClose={() => setMenuOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <StyledNavigation>
        <div className="flex items-center flex-1 min-w-0 max-md:order-2 max-md:basis-full">
          <NavigationPrimaryLinks />
          {permissions.salesAccounts.canChange() && (
            <div className="ml-4 w-[300px] max-md:ml-0 max-md:w-full">
              <CustomerSelect />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end flex-1 min-w-0 max-md:order-1 max-md:basis-full max-md:justify-start max-md:overflow-x-auto scrollbar-none">
          <Stack
            spacing={3}
            direction="horizontal"
            divider={<div className="border-r border-gray3" />}
          >
            {!isRestricted && (
              <div className="flex items-center gap-2">
                <NavigationSyncDropdown />
                <NavigationCreateDropdown />
              </div>
            )}
            {canCreateDemoAccount && (
              <NavigationIconButton
                tooltip="Create demo account"
                icon={FaUserPlus}
                onClick={() => {
                  dispatch(openModal({ name: "createDemoAccount" }));
                }}
              />
            )}
            {isITDAdminForSalesAccounts && (
              <NavigationIconButton
                tooltip="Connexx Tester"
                icon={FaRobot}
                onClick={() => {
                  // open url https://connexx-tester.vercel.app/ in a new tab
                  window.open("https://connexx-tester.vercel.app/", "_blank");
                }}
              />
            )}
            <NavigationIconButton
              tooltip="Labels"
              icon={FaPrint}
              onClick={() => {
                dispatch(openModal({ name: "labels", type: "drawer" }));
              }}
            />
            {!isStaff && <NotificationBell />}
            <NavigationUser />
          </Stack>
        </div>
      </StyledNavigation>
    </>
  );
};
